// Home's action grid (docs/features/action-grid.md — Josh, 09-29: "instead of tons of dropdowns, why can't we have
// bigger icons like a phone app screen"): Home's More and each Mac's ⋯ open a sheet of big labeled icon buttons, three
// per row (labels on up to three lines; on a phone in Snap, full-width rows), sections instead of submenus (Tools ·
// Windows · Settings; This Mac), a group’s choices as chips under its
// title (Sort, Layout, Links Open On), destructive last in red. A tap acts and the sheet closes; a chip changes its
// setting and the sheet stays. The three tapped most float to a top row, counted per sheet kind. The headset's is
// Home/ActionGrid.swift; on a phone-width screen it is a bottom sheet (grid.css).
//
// The tiles are the registered Home items (core/home-items.js), so a feature's item lands here with its icon and
// label. Seam: home.js `popover()` draws `moreGrid(home)` / `macGrid(home, key)`; its click handler calls `bumpUse`.
import { icon } from '../../core/icons.js'
import { homeItems } from '../../core/home-items.js'
import { PLACES } from '../macwindow/links.js'
import { GROUPINGS } from './claude-groups.js'
import { useStyles } from '../markdown/markdown.js'

export const gridStyles = useStyles('windows/home/grid.css')

const esc = (s) => String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]))
const PER_ROW = 3   // as the headset's ActionGrid.columns (10-02): the Most Used row is one row of them
const SORTS = [['recent', 'Recent'], ['needs', 'Needs you'], ['cost', 'Cost']]
/// More's sections: an item says its own (`group`), these are the ones that predate it; anything else is a window.
const GROUP = { tools: 'tools', totest: 'tools', addserver: 'settings', whatsnew: 'settings' }

// MARK: most used, per sheet kind ('more' | 'mac')

const useKey = (kind) => 'home.grid.use.' + kind
function uses(kind) { try { return JSON.parse(localStorage.getItem(useKey(kind)) || '{}') || {} } catch { return {} } }
export function bumpUse(kind, id) {
  const all = uses(kind)
  all[id] = (all[id] || 0) + 1
  try { localStorage.setItem(useKey(kind), JSON.stringify(all)) } catch {}
}
/// The sheet kind an open menu is, if it is a grid: 'more', or 'mac' for any Mac's ⋯.
export const gridKind = (menu) => (menu === 'more' ? 'more' : menu?.startsWith('mac:') ? 'mac' : null)
const topUsed = (kind) => Object.entries(uses(kind)).filter(([, n]) => n > 0).sort((a, b) => b[1] - a[1] || (a[0] < b[0] ? -1 : 1)).slice(0, PER_ROW).map(([id]) => id)

// MARK: the sheet

const tile = (t) => `<button class="ag-tile${t.danger ? ' danger' : ''}" data-act="${esc(t.act)}" data-use="${t.danger ? '' : esc(t.id)}" title="${esc(t.label)}" aria-label="${esc(t.label)}" ${t.disabled ? 'disabled' : ''}>` +
  `<img src="${icon(t.symbol, t.danger ? 'var(--c-red)' : 'var(--c-ink)')}"><span class="ag-name">${esc(t.label)}${t.line ? `<span class="ag-line">${esc(t.line)}</span>` : ''}</span>${t.pill ? `<span class="ag-pill">${esc(t.pill)}</span>` : ''}</button>`
const tiles = (list) => (list.length ? `<div class="ag-tiles">${list.map(tile).join('')}</div>` : '')
const chips = (group) => `<div class="ag-sub">${esc(group.title)}</div><div class="ag-chips">${group.items.map((c) =>
  `<button class="ht-chip${c.on ? ' on' : ''}" data-act="${esc(c.act)}" aria-pressed="${c.on ? 'true' : 'false'}">${esc(c.label)}</button>`).join('')}</div>`

/// `sections`: [{ title, tiles: [{ id, act, label, symbol, disabled, danger, pill, line }], chips: [{ title, items: [{ act, label, on }] }] }].
/// A tile among the kind's four most used moves to Most Used at the top (never a red one); an empty section is left out.
export function actionGrid({ kind, title, sections }) {
  const all = sections.flatMap((s) => s.tiles || [])
  const most = topUsed(kind).map((id) => all.find((t) => t.id === id && !t.danger && !t.disabled)).filter(Boolean)
  const floated = new Set(most.map((t) => t.id))
  const section = (s) => {
    const rest = (s.tiles || []).filter((t) => !floated.has(t.id))
    const groups = (s.chips || []).filter((g) => g.items.length)
    if (!rest.length && !groups.length) return ''
    return `<div class="ag-sec">${s.title ? `<div class="ag-h">${esc(s.title)}</div>` : ''}${groups.map(chips).join('')}${tiles(rest)}</div>`
  }
  const body = (most.length ? `<div class="ag-sec"><div class="ag-h">Most Used</div>${tiles(most)}</div>` : '') + sections.map(section).join('')
  return `<div class="scrim"></div><div class="sheet ag" data-grid="${esc(kind)}" role="dialog" aria-label="${esc(title)}">` +
    `<div class="ag-bar"><div class="ag-title">${esc(title)}</div><button class="ht-lb" data-act="grid:close" title="Close" aria-label="Close"><img src="${icon('xmark')}"><span class="ht-lbl">Done</span></button></div>` +
    `<div class="ag-body" data-scroll="grid">${body}</div></div>`
}

/// Home's More: the toolbar's items past New Session and More's own (To Test), by section; Sort, Group and Layout as chips.
export function moreGrid(home) {
  const offers = home.newOffers()
  const items = [...homeItems('toolbar').filter((i) => i.id !== 'newsession'), ...homeItems('more').filter((i) => home.allowed(i.needs))]
  const of = (group) => items.filter((i) => (i.group || GROUP[i.id] || 'windows') === group).map((i) => ({
    id: i.id, act: 'open:' + i.id, label: i.label, symbol: i.symbol, disabled: !home.allowed(i.needs),
    pill: i.id === 'widgets' && offers ? `${offers} offered` : '',
  }))
  const macs = home.macs().length
  const settings = [
    { title: 'Sort', items: macs ? SORTS.map(([k, label]) => ({ act: 'sort:' + k, label, on: home.sort === k })) : [] },
    // Group: By folder · Claude groups (the Claude app's sidebar groups for an extension's rows; claude-groups.js)
    { title: 'Group', items: macs ? GROUPINGS.map(([k, label]) => ({ act: 'group:' + k, label, on: home.grouping === k })) : [] },
    { title: 'Layout', items: macs > 1 ? [['tabs', 'Tabs'], ['columns', 'Columns']].map(([k, label]) => ({ act: 'layout:' + k, label, on: home.layoutPick === k })) : [] },
  ]
  return actionGrid({ kind: 'more', title: 'More', sections: [
    { title: 'Tools', tiles: of('tools') },
    { title: 'Windows', tiles: of('windows') },
    { title: 'Settings', chips: settings, tiles: of('settings') },
  ] })
}

/// A Mac's ⋯: its panels (the registered 'mac' items), Links Open On as chips, then Pause and — last, red — Forget.
export function macGrid(home, key) {
  const m = home.macOf(key)
  if (!m) return ''
  const paused = !!m.paused
  const select = home.layout === 'columns' && !paused
    ? [{ id: 'select', act: 'select:' + key, label: home.selecting?.key === key ? 'Done Selecting' : 'Select Sessions', symbol: 'checkmark.circle' }] : []
  const panels = paused ? [] : homeItems('mac').map((i) => ({ id: i.id, act: `macpanel:${i.id}|${key}`, label: i.label, symbol: i.symbol, line: typeof i.line === 'function' ? i.line() : '' }))
  const now = home.settings?.get?.('links.openOn', 'ask') || 'ask'
  const links = { title: 'Links Open On', items: paused ? [] : PLACES.map(([k, label]) => ({ act: 'linksopen:' + k, label, on: now === k })) }
  const pause = paused ? { id: 'resume', act: 'resume:' + key, label: 'Resume', symbol: 'lucide:play' } : { id: 'pause', act: 'pause:' + key, label: 'Pause This Mac', symbol: 'lucide:pause' }
  return actionGrid({ kind: 'mac', title: m.name, sections: [
    { title: 'This Mac', tiles: [...select, ...panels] },
    { title: 'Settings', chips: [links] },
    { title: '', tiles: [pause, { id: 'forget', act: 'forget:' + key, label: 'Forget This Mac', symbol: 'xmark.circle', danger: true }] },
  ] })
}
